import { ShieldCheck, AlertTriangle, BarChart2, Zap, CheckCircle2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { dashboardTimeRangeDescription, type DashboardTimeRange } from '../lib/timeRange';

interface KpiCardProps {
  title: string;
  value: string | number;
  icon: ReactNode;
  color: string;
  iconBg: string;
  subtitle?: string;
  badge?: string;
  onClick?: () => void;
}

function KpiCard({ title, value, icon, color, iconBg, subtitle, badge, onClick }: KpiCardProps) {
  return (
    <div
      className={`bg-card rounded-xl p-5 shadow kpi-hover relative ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      {badge && (
        <span className="absolute top-2 right-2 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded tracking-wide">{badge}</span>
      )}
      <div className="flex justify-between items-start mb-3">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide leading-tight">{title}</span>
        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${iconBg}`}>
          {icon}
        </div>
      </div>
      <div className={`text-4xl font-bold mb-2 ${color}`}>{value}</div>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

interface KpiGridProps {
  secureScore: string;
  highIncidents: number;
  totalIncidents: number;
  activeIncidents: number;
  resolvedIncidents: number;
  onFilterSeverity: (s: string) => void;
  onFilterStatus: (s: string) => void;
  isDemo?: boolean;
  dateFilter?: DashboardTimeRange;
}

function periodLabel(range: DashboardTimeRange | undefined): string {
  return dashboardTimeRangeDescription(range ?? '1h');
}

export function KpiGrid({ secureScore, highIncidents, totalIncidents, activeIncidents, resolvedIncidents, onFilterSeverity, onFilterStatus, dateFilter }: KpiGridProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
      <KpiCard
        title="Secure Score"
        value={secureScore}
        icon={<ShieldCheck size={16} strokeWidth={2} className="text-blue-600 dark:text-blue-400" />}
        iconBg="bg-blue-50 dark:bg-blue-950"
        color="text-primary"
        subtitle="Microsoft 365 Security"
      />
      <KpiCard
        title="High Severity Incidents"
        value={highIncidents}
        icon={<AlertTriangle size={16} strokeWidth={2} className="text-red-600 dark:text-red-400" />}
        iconBg="bg-red-50 dark:bg-red-950"
        color="text-red-600 dark:text-red-400"
        subtitle="Sentinel Workspace"
        onClick={() => onFilterSeverity('High')}
      />
      <KpiCard
        title="Total Incidents"
        value={totalIncidents}
        icon={<BarChart2 size={16} strokeWidth={2} className="text-orange-600 dark:text-orange-400" />}
        iconBg="bg-orange-50 dark:bg-orange-950"
        color="text-orange-500"
        subtitle={periodLabel(dateFilter)}
        onClick={() => onFilterSeverity('all')}
      />
      <KpiCard
        title="Active Incidents"
        value={activeIncidents}
        icon={<Zap size={16} strokeWidth={2} className="text-purple-600 dark:text-purple-400" />}
        iconBg="bg-purple-50 dark:bg-purple-950"
        color="text-purple-600 dark:text-purple-400"
        subtitle="Needs attention"
        onClick={() => onFilterStatus('Active')}
      />
      <KpiCard
        title="Resolved (Closed)"
        value={resolvedIncidents}
        icon={<CheckCircle2 size={16} strokeWidth={2} className="text-green-600 dark:text-green-400" />}
        iconBg="bg-green-50 dark:bg-green-950"
        color="text-green-600 dark:text-green-400"
        subtitle="Resolution rate"
        onClick={() => onFilterStatus('Closed')}
      />
    </div>
  );
}
